/**
 * «Выписки → Покрытие»: люди × банки на одном экране. В ячейке — счета
 * человека в этом банке: до какой даты дошла выписка и когда её загрузили.
 * Пустая ячейка из обычного набора — «нет счёта»: видно, чего не хватает.
 * Спека: `docs/specs/2026-09-30-statement-coverage-design.md`.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router";

import { NoAccess } from "@/components/NoAccess";
import { STATEMENTS, useCan } from "@/lib/access";
import {
  type CoverageAccount,
  type CoveragePerson,
  KIND_LABEL,
  loadCoverage,
  SLOT_LABEL,
} from "@/lib/coverage";
import { mskTime } from "@/lib/dates";

const TH = "border-b border-border px-2.5 py-2 text-left align-bottom";
const TD = "border-b border-border px-2 py-1.5 align-top";
const DAY = 24 * 60 * 60 * 1000;

function day(iso: string | null): string {
  return iso ? iso.split("-").reverse().join(".") : "—";
}

/** Сколько дней от конца выписки до сегодня (по Москве). */
function lag(coveredTo: string): number {
  const today = new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Moscow" });
  return Math.round((Date.parse(today) - Date.parse(coveredTo)) / DAY);
}

/** Цвет — по свежести: ≤ 7 дней норма, ≤ 30 пора обновить, старше — отстаёт. */
function tone(a: CoverageAccount): string {
  if (a.covered_to === null) return "border-err/40 bg-err-bg text-err";
  const days = lag(a.covered_to);
  if (days <= 7) return "border-ok/40 bg-ok-bg";
  if (days <= 30) return "border-warn/40 bg-warn-bg";
  return "border-err/40 bg-err-bg";
}

/** Хвост номера; у счёта Т-Банка без номера — продукт. */
function title(a: CoverageAccount): string {
  if (a.number) return `•• ${a.number.slice(-4)}${a.product ? ` · ${a.product}` : ""}`;
  return a.product ?? "номер неизвестен";
}

function AccountChip({ a, showBank }: { a: CoverageAccount; showBank: boolean }) {
  const hint = [a.number ?? a.product, a.bank_name, a.last_operation ? `последняя операция ${day(a.last_operation)}` : null]
    .filter(Boolean)
    .join(" · ");
  return (
    <div title={hint} className={`rounded-[var(--radius-ui)] border px-1.5 py-1 leading-tight ${tone(a)}`}>
      <div className="whitespace-nowrap font-medium tabular-nums">{title(a)}</div>
      {showBank && a.bank_name ? <div className="truncate text-[10.5px] text-fg-muted">{a.bank_name}</div> : null}
      {a.covered_to ? (
        <>
          <div className="whitespace-nowrap tabular-nums">
            по {day(a.covered_to)}
            {lag(a.covered_to) > 7 ? <span className="text-fg-muted"> · {lag(a.covered_to)} дн.</span> : null}
          </div>
          <div className="whitespace-nowrap text-[10.5px] tabular-nums text-fg-muted">
            загр. {mskTime(a.uploaded_at)}
            {a.statements > 1 ? ` · файлов ${a.statements}` : ""}
          </div>
        </>
      ) : (
        <div className="whitespace-nowrap">нет выписки</div>
      )}
    </div>
  );
}

export function CoveragePage() {
  return useCan(STATEMENTS) ? <CoverageScreen /> : <NoAccess what="выпискам" />;
}

function CoverageScreen() {
  const [people, setPeople] = useState<CoveragePerson[] | null>(null);
  const [failure, setFailure] = useState("");

  useEffect(() => {
    loadCoverage()
      .then(setPeople)
      .catch((error: Error) => setFailure(error.message));
  }, []);

  // Колонки одинаковы у всех людей — берём у первого.
  const blocks = people?.[0]?.blocks ?? [];

  return (
    <main className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4 [&>*]:flex-none">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[12.5px] text-fg-muted">
        <span>
          По каждому счёту: до какой даты дошла последняя выписка и когда её загрузили. Файлы — в{" "}
          <Link to="/statements" className="underline">
            «Загрузке»
          </Link>
          .
        </span>
        <span className="flex items-center gap-1.5">
          <i className="size-2.5 rounded-sm border border-ok/40 bg-ok-bg" /> ≤ 7 дн.
          <i className="ml-2 size-2.5 rounded-sm border border-warn/40 bg-warn-bg" /> ≤ 30 дн.
          <i className="ml-2 size-2.5 rounded-sm border border-err/40 bg-err-bg" /> старше или нет выписки
        </span>
      </div>
      {failure ? (
        <div role="alert" className="rounded-[var(--radius-ui)] border border-err/30 bg-err-bg px-3 py-2 text-[12.5px] text-err">
          {failure}
        </div>
      ) : null}
      {people === null ? (
        <div className="text-[12.5px] text-fg-muted">…</div>
      ) : people.length === 0 ? (
        <div className="text-[12.5px] text-fg-muted">
          Людей нет: заведите их в{" "}
          <Link to="/statements/entrepreneurs" className="underline">
            справочнике ИП
          </Link>
          .
        </div>
      ) : (
        <div className="overflow-x-auto rounded-[var(--radius-card)] border border-border">
          <table className="w-full border-collapse text-[12px]">
            <thead>
              <tr className="bg-bg-subtle text-[11px] font-medium text-fg-muted">
                <th className={TH} rowSpan={2}>
                  человек
                </th>
                {blocks.map((b) => (
                  <th key={b.kind} colSpan={b.cells.length} className={`${TH} border-l text-center text-fg`}>
                    {KIND_LABEL[b.kind]}
                  </th>
                ))}
              </tr>
              <tr className="bg-bg-subtle text-[11px] font-medium text-fg-muted">
                {blocks.flatMap((b) =>
                  b.cells.map((c, i) => (
                    <th key={`${b.kind}-${c.slot}`} className={`${TH} ${i === 0 ? "border-l" : ""}`}>
                      {SLOT_LABEL[c.slot]}
                    </th>
                  )),
                )}
              </tr>
            </thead>
            <tbody>
              {people.map((p) => (
                <tr key={p.id}>
                  <td className={`${TD} whitespace-nowrap font-medium`}>{p.name}</td>
                  {p.blocks.flatMap((b) =>
                    b.cells.map((c, i) => (
                      <td key={`${b.kind}-${c.slot}`} className={`${TD} min-w-[128px] ${i === 0 ? "border-l" : ""}`}>
                        {c.accounts.length === 0 ? (
                          c.slot === "other" ? (
                            <span className="text-fg-faint">—</span>
                          ) : (
                            <div className="rounded-[var(--radius-ui)] border border-dashed border-border-strong px-1.5 py-1 text-fg-faint">
                              нет счёта
                            </div>
                          )
                        ) : (
                          <div className="grid gap-1">
                            {c.accounts.map((a) => (
                              <AccountChip key={a.key} a={a} showBank={c.slot === "other"} />
                            ))}
                          </div>
                        )}
                      </td>
                    )),
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}
